import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, Switch, Space,
    Typography, message, Popconfirm, Spin, InputNumber, Tag, DatePicker
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import { certificationAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

export default function CertificationsPage() {
    const [certifications, setCertifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCert, setEditingCert] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCertifications();
    }, []);

    const fetchCertifications = async () => {
        try {
            setLoading(true);
            const response = await certificationAPI.getAll();
            console.log('Certifications data:', response.data);
            setCertifications(response.data || []);
        } catch (error) {
            console.error('Error fetching certifications:', error);
            message.error('Failed to fetch certifications');
            setCertifications([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (cert = null) => {
        if (cert) {
            setEditingCert(cert);
            form.setFieldsValue({
                name: cert.name,
                certification: cert.certification,
                level: cert.level,
                validUntil: cert.validUntil ? dayjs(cert.validUntil) : null,
                association: cert.association || '',
                order: cert.order || 0,
                isActive: cert.isActive
            });
        } else {
            setEditingCert(null);
            form.setFieldsValue({
                name: '',
                certification: '',
                level: 'Senior',
                validUntil: null,
                association: '',
                order: certifications.length + 1,
                isActive: true
            });
        }
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setEditingCert(null);
        form.resetFields();
    };

    const handleSubmit = async (values) => {
        setSaving(true);

        try {
            // Convert date to ISO string if exists
            const submitData = {
                ...values,
                validUntil: values.validUntil ? values.validUntil.toISOString() : null
            };

            if (editingCert) {
                const idToUse = editingCert.id || editingCert._id;
                await certificationAPI.update(idToUse, submitData);
                message.success('Sertifikasi berhasil diupdate!');
            } else {
                await certificationAPI.create(submitData);
                message.success('Sertifikasi berhasil ditambahkan!');
            }
            fetchCertifications();
            handleCloseModal();
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await certificationAPI.delete(id);
            message.success('Sertifikasi berhasil dihapus!');
            fetchCertifications();
        } catch (error) {
            message.error('Gagal menghapus sertifikasi');
        }
    };

    const levelColors = {
        'Senior': 'red',
        'Madya': 'orange',
        'Junior': 'green',
    };

    const columns = [
        {
            title: 'No',
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Nama Engineer',
            dataIndex: 'name',
            key: 'name',
            width: '25%',
        },
        {
            title: 'Sertifikasi',
            dataIndex: 'certification',
            key: 'certification',
            width: '20%',
        },
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            render: (level) => (
                <Tag color={levelColors[level] || 'default'}>
                    {level}
                </Tag>
            ),
        },
        {
            title: 'Masa Berlaku',
            dataIndex: 'validUntil',
            key: 'validUntil',
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
        },
        {
            title: 'Asosiasi',
            dataIndex: 'association',
            key: 'association',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'default'}>
                    {isActive ? 'Aktif' : 'Non-Aktif'}
                </Tag>
            ),
        },
        {
            title: 'Urutan',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        {
            title: 'Aksi',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenModal(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Hapus Sertifikasi"
                        description="Yakin ingin menghapus sertifikasi ini?"
                        onConfirm={() => handleDelete(record.id || record._id)}
                        okText="Ya"
                        cancelText="Batal"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            Hapus
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading certifications..." />
            </div>
        );
    }

    return (
        <div>
            <div style={{
                marginBottom: 24,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Title level={2}>
                    <SafetyCertificateOutlined /> Sertifikasi Kompetensi
                </Title>
                <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenModal()}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    }}
                >
                    Tambah Sertifikasi
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={certifications}
                rowKey={(record) => record.id || record._id}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} sertifikasi`,
                }}
                locale={{
                    emptyText: 'Belum ada data sertifikasi',
                }}
                style={{
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
            />

            <Modal
                title={
                    <Space>
                        <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>
                            {editingCert ? 'Edit Sertifikasi' : 'Tambah Sertifikasi'}
                        </span>
                    </Space>
                }
                open={modalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={600}
                centered
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: 24 }}
                >
                    <Form.Item
                        name="name"
                        label="Nama Engineer"
                        rules={[{ required: true, message: 'Nama engineer harus diisi!' }]}
                    >
                        <Input
                            size="large"
                            placeholder="Contoh: Engineer 1"
                            prefix={<SafetyCertificateOutlined style={{ color: '#bfbfbf' }} />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="certification"
                        label="Sertifikasi"
                        rules={[{ required: true, message: 'Sertifikasi harus diisi!' }]}
                    >
                        <Input
                            size="large"
                            placeholder="Contoh: Sertifikat Keahlian Struktur"
                        />
                    </Form.Item>

                    <Form.Item
                        name="level"
                        label="Level"
                        rules={[{ required: true, message: 'Level harus dipilih!' }]}
                    >
                        <Select size="large" placeholder="Pilih Level">
                            <Option value="Senior">Senior</Option>
                            <Option value="Madya">Madya</Option>
                            <Option value="Junior">Junior</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="validUntil"
                        label="Masa Berlaku"
                        tooltip="Tanggal berakhirnya sertifikasi"
                    >
                        <DatePicker
                            size="large"
                            style={{ width: '100%' }}
                            placeholder="Pilih tanggal"
                            format="DD MMM YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="association"
                        label="Asosiasi"
                        tooltip="Nama asosiasi penerbit sertifikasi"
                    >
                        <Input
                            size="large"
                            placeholder="Contoh: LPJK, BNSP, dll"
                        />
                    </Form.Item>

                    <Form.Item
                        name="order"
                        label="Urutan"
                        tooltip="Urutan tampilan di website"
                    >
                        <InputNumber
                            size="large"
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="1"
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Status"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Aktif"
                            unCheckedChildren="Non-Aktif"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                size="large"
                                onClick={handleCloseModal}
                                style={{ minWidth: 100 }}
                            >
                                Batal
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                loading={saving}
                                style={{
                                    minWidth: 100,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                }}
                            >
                                {editingCert ? 'Update' : 'Simpan'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
